import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Award, Sparkles, Trophy, Star, Gem, Crown, Lock, Check } from "lucide-react";

const ICONS = {
    silver: Award,
    gold: Star,
    diamond: Gem,
    premium: Crown,
};

export default function Loyalty() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/sponsorships/loyalty").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="p-12 text-zinc-500">Chargement…</div>;

    const cur = data.current_level;
    const nxt = data.next_level;

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-12">
            <header>
                <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                    Programme fidélité
                </div>
                <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight leading-none">
                    Sponsormania.fr <span style={{ color: cur.color }}>{cur.label}</span>
                </h1>
                <p className="text-zinc-400 mt-3 max-w-2xl">
                    Plus vous soutenez les clubs, plus vous gagnez de points et débloquez des avantages exclusifs.
                </p>
            </header>

            {/* Stats hero card */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div
                    className="lg:col-span-7 card-tu relative overflow-hidden"
                    style={{
                        background: `linear-gradient(135deg, ${cur.color}22 0%, #18181b 60%)`,
                        borderColor: `${cur.color}66`,
                    }}
                >
                    <div className="flex items-start gap-6 mb-6">
                        <div
                            className="w-20 h-20 flex items-center justify-center border-2 shrink-0"
                            style={{ borderColor: cur.color, color: cur.color }}
                        >
                            {React.createElement(ICONS[cur.key] || Award, { size: 40 })}
                        </div>
                        <div className="flex-1">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
                                Votre niveau actuel
                            </div>
                            <div
                                className="font-display text-6xl uppercase tracking-tight leading-none"
                                style={{ color: cur.color }}
                                data-testid="loyalty-level-label"
                            >
                                {cur.label}
                            </div>
                            <div className="mt-3 text-zinc-300">
                                <span data-testid="loyalty-points" className="font-display text-3xl text-white">
                                    {data.points}
                                </span>
                                <span className="text-zinc-500 text-sm uppercase tracking-widest ml-2">points</span>
                            </div>
                        </div>
                    </div>

                    {/* Progress to next level */}
                    {nxt ? (
                        <div>
                            <div className="flex justify-between mb-2 text-xs">
                                <span className="text-zinc-400">
                                    Vers <span style={{ color: nxt.color }}>{nxt.label}</span>
                                </span>
                                <span className="text-zinc-400">
                                    {nxt.min_points - data.points} points restants
                                </span>
                            </div>
                            <div className="w-full h-3 bg-zinc-900 border border-zinc-800 overflow-hidden">
                                <div
                                    className="h-full transition-all duration-1000"
                                    style={{
                                        width: `${data.progress_pct}%`,
                                        background: `linear-gradient(90deg, ${cur.color}, ${nxt.color})`,
                                        boxShadow: `0 0 15px ${nxt.color}`,
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-orange-500 font-bold uppercase tracking-widest text-xs">
                            🏆 Niveau maximum atteint
                        </div>
                    )}
                </div>

                <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
                    <Stat label="Maillots sponsorisés" value={data.stats.total_jerseys_sponsored} />
                    <Stat label="Investi" value={`${data.stats.total_invested}€`} />
                    <Stat label="Clubs aidés" value={data.stats.total_clubs} />
                    <Stat label="Sponsorings" value={data.stats.total_sponsorships} />
                </div>
            </div>

            {/* Discount rule banner */}
            <div className="card-tu border-l-4 border-l-orange-500 flex items-start gap-4">
                <Sparkles className="text-orange-500 shrink-0" />
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                        Remise volume automatique
                    </div>
                    <p className="text-zinc-200">
                        <span className="font-bold">−{data.discount_rule.discount_pct}%</span> sur tout sponsoring
                        couvrant plus de <span className="font-bold">{data.discount_rule.threshold_jerseys} maillots</span>{" "}
                        d'un même club.
                    </p>
                </div>
            </div>

            {/* Level grid */}
            <section>
                <h2 className="font-display text-4xl uppercase tracking-tight mb-6">
                    Tous les niveaux
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data.levels.map((lvl) => {
                        const Icon = ICONS[lvl.key] || Award;
                        const unlocked = data.points >= lvl.min_points;
                        const isCurrent = lvl.key === cur.key;
                        return (
                            <div
                                key={lvl.key}
                                data-testid={`level-${lvl.key}`}
                                className={[
                                    "card-tu relative",
                                    isCurrent ? "border-orange-500/60 shadow-[0_0_24px_rgba(234,88,12,0.2)]" : "",
                                ].join(" ")}
                                style={isCurrent ? {} : { borderColor: unlocked ? `${lvl.color}55` : undefined }}
                            >
                                {isCurrent && (
                                    <div className="absolute -top-3 left-4 bg-orange-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest">
                                        Actuel
                                    </div>
                                )}
                                <div className="flex items-start justify-between mb-4">
                                    <div
                                        className="w-12 h-12 flex items-center justify-center border-2"
                                        style={{
                                            borderColor: unlocked ? lvl.color : "#27272a",
                                            color: unlocked ? lvl.color : "#52525b",
                                        }}
                                    >
                                        <Icon size={24} />
                                    </div>
                                    {!unlocked && <Lock className="text-zinc-700" size={16} />}
                                </div>
                                <div
                                    className="font-display text-3xl uppercase tracking-tight"
                                    style={{ color: unlocked ? lvl.color : "#52525b" }}
                                >
                                    {lvl.label}
                                </div>
                                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-4">
                                    À partir de {lvl.min_points} pts
                                </div>
                                <ul className="space-y-1.5 text-sm text-zinc-300">
                                    {lvl.perks.map((p) => (
                                        <li key={p} className="flex items-start gap-2">
                                            {unlocked ? (
                                                <Check size={14} className="text-orange-500 mt-1 shrink-0" />
                                            ) : (
                                                <span className="text-zinc-700 mt-0.5">·</span>
                                            )}
                                            <span className={unlocked ? "" : "text-zinc-600"}>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="text-center pt-4">
                <Link to="/clubs" className="btn-primary" data-testid="explore-from-loyalty">
                    <Trophy size={16} /> Continuer à sponsoriser
                </Link>
            </section>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-[#09090b] p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                {label}
            </div>
            <div className="font-display text-4xl text-orange-500 leading-none">{value}</div>
        </div>
    );
}
